import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { pluginsApi } from "@/api/plugins-api"
import { Button } from "@/components/ui/button"

import { usePluginQueryKey } from "./use-plugin-query-key"

export function ConnectionGrants({ connectionId }: { connectionId: string }) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const grants = useQuery({
    queryKey: pluginKey("grants", connectionId),
    queryFn: () => pluginsApi.grants(connectionId),
    enabled: open,
  })
  const revoke = useMutation({
    mutationFn: (id: string) => pluginsApi.revokeGrant(connectionId, id),
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey("grants", connectionId) }),
  })
  return (
    <details
      className="rounded-lg border p-3"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer text-sm">{t("plugins.backgroundManage")}</summary>
      {grants.isPending && open && <p role="status">{t("plugins.loading")}</p>}
      {grants.data?.consents.length === 0 && (
        <p className="text-muted-foreground mt-2 text-sm">{t("plugins.backgroundNone")}</p>
      )}
      {grants.data?.consents.map((grant) => (
        <div
          key={grant.id}
          className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm"
        >
          <span className="max-w-full truncate">
            {grant.flow_title || t("plugins.backgroundFlow", { id: grant.flow_id })}
          </span>
          <Button
            disabled={revoke.isPending}
            size="sm"
            variant="outline"
            onClick={() => revoke.mutate(grant.id)}
          >
            {t("plugins.backgroundRevoke")}
          </Button>
        </div>
      ))}
      {(grants.error || revoke.error) && (
        <p className="text-destructive text-sm" role="alert">
          {t("plugins.error")}
        </p>
      )}
    </details>
  )
}
